/**
 * The name of the document this product issued, in the forms the suggested post needs.
 *
 * The rule is the backend's (`backend/src/domain/document-noun.ts`) and it stays the backend's:
 * this module does not decide anything, it reproduces. The two are held together by
 * `tests/fixtures/document-noun-vectors.json`, which both packages run against case by case — the
 * same arrangement PO 7.9 uses for the Add-to-Profile link and the share card's dimensions.
 *
 * Turkish suffixes are not concatenation: a title that already ends in «Sertifikası» must not
 * become «Sertifikası sertifikasını». The noun is recognised at the END of the record's own title
 * and REPLACED, and a title ending in a noun this module does not know keeps its own words.
 */
import { trLower } from './reference';

export interface DocumentNoun {
  ending: string;
  yours: string;
  accusative: string;
  bare: string;
}

export const DOCUMENT_NOUNS: readonly DocumentNoun[] = [
  { ending: 'Sertifikası', yours: 'Sertifikanız', accusative: 'Sertifikasını', bare: 'Sertifikayı' },
  { ending: 'Rozeti', yours: 'Rozetiniz', accusative: 'Rozetini', bare: 'Rozeti' },
  { ending: 'Diploması', yours: 'Diplomanız', accusative: 'Diplomasını', bare: 'Diplomayı' },
  { ending: 'Belgesi', yours: 'Belgeniz', accusative: 'Belgesini', bare: 'Belgeyi' },
];

export function nounOf(title: string): DocumentNoun | null {
  return DOCUMENT_NOUNS.find((n) => title.endsWith(n.ending)) ?? null;
}

const replaceEnding = (title: string, noun: DocumentNoun, form: string): string =>
  `${title.slice(0, title.length - noun.ending.length)}${form}`;

export function possessiveTitle(title: string): string {
  const noun = nounOf(title);
  return noun ? replaceEnding(title, noun, noun.yours) : title;
}

export function accusativeTitle(title: string): string {
  const noun = nounOf(title);
  return noun ? replaceEnding(title, noun, noun.accusative) : title;
}

/**
 * «CXO 100 Seçici Kurul Üyesi sertifikasını» — the form a sentence uses mid-clause.
 *
 * The document's type is a common noun inside a sentence, so only the NOUN is lowered and the
 * record's own name keeps its capitals. `trLower` is the product's own Turkish-aware folding, so
 * an `I` becomes `ı` rather than `i`.
 */
export function accusativeTitleLower(title: string): string {
  const noun = nounOf(title);
  if (!noun) return title;
  const acc = noun.accusative;
  return replaceEnding(title, noun, trLower(acc.slice(0, 1)) + acc.slice(1));
}

export function bareNoun(title: string): string {
  return nounOf(title)?.bare ?? 'Belgeyi';
}

/** Complete only display copy when the immutable title omits its document noun. */
export function titleWithDocumentNoun(title: string, credentialType?: string): string {
  if (nounOf(title)) return title;
  const endings: Record<string, string> = {
    digital_badge: 'Rozeti',
    certificate: 'Sertifikası',
    participation: 'Katılım Belgesi',
    achievement: 'Başarı Belgesi',
    organization_certificate: 'Sertifikası',
    membership: 'Rozeti',
    diploma: 'Diploması',
  };
  const ending =
    credentialType && Object.hasOwn(endings, credentialType) ? endings[credentialType] : undefined;
  return ending ? `${title} ${ending}` : title;
}
